import { parseEuroCents } from '../money'

/**
 * S9 (ADM-4): a future booking made on the old site, carried over by hand, as
 * the counter types it. The server keeps every rule; this reads the four
 * fields the way its request does, so the form says what is missing before it
 * asks, and shows back exactly the amounts it is about to send.
 */
export interface OldSiteFields {
  reference: string
  agreedTotal: string
  paid: string
  stripePaymentIntentId: string
}

export const EMPTY_OLD_SITE: OldSiteFields = {
  reference: '',
  agreedTotal: '',
  paid: '',
  stripePaymentIntentId: '',
}

/** What stops the booking from being carried over yet, in the section's order. */
export type OldSiteGap =
  | 'reference'
  | 'agreedTotal'
  | 'paid'
  | 'stripePaymentIntentId'
  | 'paidForStripe'

/** The request's own limits (StaffOldSiteBookingRequest). */
export const OLD_SITE_REFERENCE_MAX = 40
const MAX_CENTS = 10_000_000
const STRIPE_PAYMENT_INTENT = /^pi_[A-Za-z0-9]+$/

export interface OldSiteRequest {
  reference: string
  agreedTotalCents: number
  paid?: { amountCents: number; stripePaymentIntentId?: string }
}

export interface OldSiteReading {
  gaps: OldSiteGap[]
  /** The request's `oldSite`, once nothing is missing. */
  request: OldSiteRequest | null
  agreedCents: number | null
  /** What was paid there: 0 when the field is empty. */
  paidCents: number
  /** What is left to pay at the pick-up; below 0, money the customer is owed. */
  balanceCents: number | null
}

function cents(text: string, minimum: number): number | null {
  const value = parseEuroCents(text.trim())
  return value !== null && value >= minimum && value <= MAX_CENTS ? value : null
}

export function readOldSite(fields: OldSiteFields): OldSiteReading {
  const gaps: OldSiteGap[] = []
  const reference = fields.reference.trim()
  if (reference === '' || reference.length > OLD_SITE_REFERENCE_MAX) gaps.push('reference')
  const agreedCents = cents(fields.agreedTotal, 0)
  if (agreedCents === null) gaps.push('agreedTotal')
  // Empty is nothing paid; anything typed must be an amount, and more than nothing.
  const paidTyped = fields.paid.trim() !== ''
  const paidCents = paidTyped ? cents(fields.paid, 1) : 0
  if (paidCents === null) gaps.push('paid')
  const intent = fields.stripePaymentIntentId.trim()
  if (intent !== '' && !STRIPE_PAYMENT_INTENT.test(intent)) gaps.push('stripePaymentIntentId')
  else if (intent !== '' && !paidTyped) gaps.push('paidForStripe')

  const request: OldSiteRequest | null =
    gaps.length === 0 && agreedCents !== null && paidCents !== null
      ? {
          reference,
          agreedTotalCents: agreedCents,
          ...(paidCents > 0
            ? {
                paid: {
                  amountCents: paidCents,
                  ...(intent ? { stripePaymentIntentId: intent } : {}),
                },
              }
            : {}),
        }
      : null
  return {
    gaps,
    request,
    agreedCents,
    paidCents: paidCents ?? 0,
    balanceCents: agreedCents === null || paidCents === null ? null : agreedCents - paidCents,
  }
}
